import { useState, useMemo, useEffect } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { AddEntityButton } from "../../../components/AddEntityButton/AddEntityButton";
import { strings } from "../../../config/string";
import { ProjectCrudModals } from "../ProjectCrudModals/ProjectCrudModals";
import { ProjectItem } from "../ProjectItem/ProjectItem";
import { ProjectForeignIdMapModal } from "../../../components/modals/ProjectForeignIdMapModal";
import { EmptyListComponent } from "../../../components/EmptyListComponent/EmptyListComponent";
import debounce from 'lodash/debounce';
import orderBy from 'lodash/orderBy';
import { Project, Folder } from "../../../types";
import { useGetAllFolders } from "../../../services/folderService";
import { useSearchParams } from "react-router-dom";

type Props = {
  projects: Project[];
  folderId?: string;
};

export const ProjectsContainer = ({ projects = [], folderId: initialFolderId }: Props) => {
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showForeignIdModal, setShowForeignIdModal] = useState(false);
  const [projectId, setProjectId] = useState<string>();

  const { data: allFolders = [] } = useGetAllFolders();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedFolderId, setSelectedFolderId] = useState<string>(searchParams.get('folderId') || initialFolderId || "");
  const [filterInput, setFilterInput] = useState(searchParams.get('searchQuery') || '');
  const [debouncedFilterInput, setDebouncedFilterInput] = useState(searchParams.get('searchQuery') || '');

  const [availableFolders, setAvailableFolders] = useState<Folder[]>([]);

  useEffect(() => {
      setAvailableFolders(allFolders);
  }, [allFolders]);

   useEffect(() => {
    const urlFolderId = searchParams.get('folderId');
    if (urlFolderId) {
        setSelectedFolderId(urlFolderId);
    } else if (initialFolderId) {
      const initialFolder = allFolders.find(f => f.id === initialFolderId);
      if (initialFolder) {
        setSelectedFolderId(initialFolderId);
      } else {
         setSelectedFolderId("");
      }
    } else {
      setSelectedFolderId("");
    }
     setAvailableFolders(allFolders);

   }, [initialFolderId, allFolders, searchParams]);

   useEffect(() => {
       const handler = debounce(() => {
           setDebouncedFilterInput(filterInput);
       }, 500);

       handler();

       return () => {
           handler.cancel();
       };
   }, [filterInput]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    if (selectedFolderId) {
      newSearchParams.set('folderId', selectedFolderId);
    }
    if (debouncedFilterInput) {
      newSearchParams.set('searchQuery', debouncedFilterInput);
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [selectedFolderId, debouncedFilterInput, setSearchParams]);

  const sortedAvailableFolders = useMemo(() => {
    return orderBy(availableFolders, [(folder) => folder.name.toLowerCase()], ['asc']);
  }, [availableFolders]);

  const filteredProjects = useMemo(() => {
    let tempProjects = projects;

    if (selectedFolderId) {
      tempProjects = tempProjects.filter((project: Project) => project.folderId === selectedFolderId);
    }

    if (debouncedFilterInput) {
      const lowerCaseFilter = debouncedFilterInput.toLowerCase();
      tempProjects = tempProjects.filter((project: Project) =>
        project.name.toLowerCase().includes(lowerCaseFilter) ||
        project.description.toLowerCase().includes(lowerCaseFilter) ||
        (project.officeName && project.officeName.toLowerCase().includes(lowerCaseFilter)) ||
        (project.folderName && project.folderName.toLowerCase().includes(lowerCaseFilter)) ||
        (project.projectStatus && project.projectStatus.toLowerCase().includes(lowerCaseFilter)) ||
        (project.projectNumber && project.projectNumber.toString().includes(lowerCaseFilter)) ||
        (project.foreignId && project.foreignId.toString().includes(lowerCaseFilter))
      );
    }

    return orderBy(tempProjects, [(project) => project.name.toLowerCase()], ['asc']);
  }, [projects, selectedFolderId, debouncedFilterInput]);

  const clearFilters = () => {
    setSelectedFolderId("");
    setFilterInput("");
    setDebouncedFilterInput("");
    setSearchParams({}, { replace: true });
  };

  const handleFilterByFolder = (folderId: string) => {
    setSelectedFolderId(folderId);
  };

  return (
    <div>
      <Row className="mb-3 mx-1 g-3 align-items-center p-3 bg-light rounded shadow-sm">
        <Col xs={12} sm={6} md={4}>
          <Form.Group controlId="projectFolderFilter">
            <Form.Label className="text-dark fw-semibold small mb-1">Filter by Folder</Form.Label>
            <Form.Select
              aria-label="Filter by Folder"
              
              value={selectedFolderId}
              onChange={(e) => setSelectedFolderId(e.target.value)}
              className="form-select-sm"
            >
              <option value="">All Folders</option>
              {sortedAvailableFolders.map((folder: Folder) => (
                <option key={folder.id} value={folder.id}>
                   {folder.name}{folder.officeName ? ` (${folder.officeName})` : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col xs={12} md={6}>
          <Form.Group controlId="projectTextFilter">
            <Form.Label className="text-dark fw-semibold small mb-1">Search Projects</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search name, desc, ID, status..."
              value={filterInput}
              onChange={(e) => setFilterInput(e.target.value)}
              className="form-control-sm bg-white text-dark"
            />
          </Form.Group>
        </Col>

        <Col xs={12} md={2} className="text-md-end align-self-end d-flex justify-content-end gap-2">
           <Button variant="outline-secondary" size="sm" onClick={clearFilters} title="Clear all filters" disabled={!selectedFolderId && !filterInput}>
               Clear
           </Button>
          <AddEntityButton onClick={() => setShowAdd(true)} text={strings.addNewProject} />
        </Col>
      </Row>

      <ProjectCrudModals
        showDelete={showDelete}
        showAdd={showAdd}
        showEdit={showEdit}
        folderId={selectedFolderId || initialFolderId}
        onAddClose={() => setShowAdd(false)}
        onDeleteClose={() => { setShowDelete(false); setProjectId(undefined); }}
        onEditClose={() => { setShowEdit(false); setProjectId(undefined); }}
        projectId={projectId}
      />

      <Row xs={1} sm={2} lg={3} xl={4} className="mt-1">
        {filteredProjects.length > 0 ? (
          filteredProjects.map((project) => (
            <ProjectItem
              key={project.id}
              project={project}
              onClickEdit={() => {
                setProjectId(project.id);
                setShowEdit(true);
              }}
              onClickDelete={() => {
                setProjectId(project.id);
                setShowDelete(true);
              }}
              onFilterByFolder={handleFilterByFolder}
            />
          ))
        ) : (
          <Col xs={12}>
            <EmptyListComponent 
              message={projects.length === 0 ? "No projects available" : "No projects found matching your criteria"}
              filterApplied={!!(selectedFolderId || debouncedFilterInput)}
              onClearFilter={clearFilters}
            />
          </Col>
        )}
      </Row>

      {projectId && (
        <ProjectForeignIdMapModal
          projectId={projectId}
          open={showForeignIdModal}
          onClose={() => { setShowForeignIdModal(false); setProjectId(undefined); }}
        />
      )}
    </div>
  );
};
